import { useCallback, useEffect, useMemo, useState } from "react";
import { api, SIZES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SoleOrderForm from "@/components/SoleOrderForm";
import SoleReceiptEditDialog from "@/components/SoleReceiptEditDialog";
import SoleReceiveDialog from "@/components/SoleReceiveDialog";
import SupplierCards from "@/components/SupplierCards";
import SupplierPicker from "@/components/SupplierPicker";
import { useData } from "@/lib/data";
import { ClipboardList, Plus, Printer, PackageCheck, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: { label: "Bekliyor", cls: "bg-[#3B82F6]/15 text-[#1D4ED8] border-[#3B82F6]/40" },
  partial: { label: "Kısmi Geldi", cls: "bg-[#F59E0B]/15 text-[#B45309] border-[#F59E0B]/40" },
  completed: { label: "Tamamı Geldi", cls: "bg-[#10B981]/15 text-[#047857] border-[#10B981]/40" },
};

const groupLines = (items) => {
  const map = new Map();
  items.forEach((i) => {
    const k = `${i.sole_code}||${i.sole_color}`;
    if (!map.has(k)) map.set(k, { key: k, code: i.sole_code, color: i.sole_color, sizes: {} });
    map.get(k).sizes[i.size] = i;
  });
  return [...map.values()];
};

export default function SoleOrders() {
  const [editing, setEditing] = useState(null);
  const [orders, setOrders] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const [filter, setFilter] = useState("open");
  const [receipts, setReceipts] = useState([]);
  const [pickSupplier, setPickSupplier] = useState(false);
  const [recvSupplierId, setRecvSupplierId] = useState("");
  const { suppliers } = useData();
  const recvSupplier = suppliers.find((s) => s.id === recvSupplierId)?.name;

  const load = useCallback(async () => {
    const [r, rc] = await Promise.all([api.get("/sole-orders"), api.get("/soles/receipts")]);
    setOrders(r.data); setReceipts(rc.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Taban siparişi silinsin mi?")) return;
    try { await api.delete(`/sole-orders/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  const shown = useMemo(() => orders.filter((o) => filter === "all" || (filter === "open" ? o.status !== "completed" : o.status === "completed")), [orders, filter]);
  const supplierOpen = useMemo(() => orders.filter((o) => o.supplier === recvSupplier && o.status !== "completed"), [orders, recvSupplier]);
  const receiptsByOrder = useMemo(() => receipts.reduce((m, r) => ({ ...m, [r.sole_order_id]: [...(m[r.sole_order_id] || []), r] }), {}), [receipts]);
  const sum = (o, f) => o.items.reduce((a, b) => a + (b[f] || 0), 0);

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="sole-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Tedarik</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
            <ClipboardList className="w-7 h-7 text-[#D97706]" /> Taban Siparişleri
          </h1>
          <p className="text-[#6B5B4A] mt-2 text-sm">Taban firmalarına verilen siparişler ve gelen tabanların takibi. Gelen tabanlar otomatik taban stoğuna eklenir.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
        <Button onClick={() => { setRecvSupplierId(""); setPickSupplier(true); }} data-testid="sole-receive-general-btn"
          className="bg-[#047857] hover:bg-[#065F46] text-white font-display uppercase tracking-wide h-11 px-5">
          <PackageCheck className="w-4 h-4 mr-2" /> Gelen Taban Girişi
        </Button>
        <Button onClick={() => setFormOpen(true)} data-testid="sole-order-new-btn"
          className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-5">
          <Plus className="w-4 h-4 mr-2" /> Yeni Taban Siparişi
        </Button>
        </div>
      </div>

      {pickSupplier && (
        <div className="rounded-lg border-2 border-[#047857]/50 bg-[#FFFBF4] p-4 mb-6" data-testid="sole-receive-general-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="font-display uppercase tracking-wide text-[#047857]">Gelen Taban Girişi · firma seçin</p>
            <button onClick={() => setPickSupplier(false)} className="text-sm text-[#8C7B68] underline" data-testid="sole-receive-general-close">Kapat</button>
          </div>
          <SupplierPicker value={recvSupplierId} onChange={setRecvSupplierId} testId="sole-receive-supplier" />
          {recvSupplier && (
            <div className="mt-3 space-y-2">
              {supplierOpen.length === 0 &&
                <p className="text-sm text-[#8C7B68]">Bu firmanın bekleyen siparişi yok.</p>}
              {supplierOpen.map((o) => {
                const ord = sum(o, "qty"), got = sum(o, "received_qty");
                return (
                  <div key={o.id} className="flex items-center justify-between rounded-md border border-[#DCCFB8] px-3 py-2">
                    <span className="text-sm font-mono">#{o.no} · {new Date(o.created_at).toLocaleDateString("tr-TR")} · verilen {ord} · gelen {got} · <b className="text-[#B45309]">bekleyen {ord - got}</b></span>
                    <Button onClick={() => setReceiving(o)} data-testid={`sole-receive-general-order-${o.id}`} className="h-8 bg-[#047857] hover:bg-[#065F46] text-white">Teslim Al</Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <SupplierCards orders={orders} onChanged={load} />

      <div className="flex gap-2 mb-4" data-testid="sole-orders-filter">
        {[["open", "Açık"], ["completed", "Tamamlanan"], ["all", "Tümü"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`sole-orders-filter-${k}`}
            className={`h-9 px-4 rounded-md text-sm border ${filter === k ? "bg-[#D97706] text-white border-[#D97706]" : "border-[#DCCFB8] bg-[#FFFBF4] text-[#6B5B4A] hover:bg-[#EFE6D6]"}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-4" data-testid="sole-orders-list">
        {shown.length === 0 && (
          <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-10 text-center text-[#8C7B68]">Bu listede taban siparişi yok.</div>
        )}
        {shown.map((o) => {
          const st = STATUS[o.status] || STATUS.pending;
          const ordered = sum(o, "qty"), got = sum(o, "received_qty");
          return (
            <div key={o.id} className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-4" data-testid={`sole-order-card-${o.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-xl font-bold uppercase tracking-tight">#{o.no} · {o.supplier}</h3>
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border ${st.cls}`} data-testid={`sole-order-status-${o.id}`}>{st.label}</span>
                  </div>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-[#8C7B68] mt-1">
                    Verildi: {new Date(o.created_at).toLocaleDateString("tr-TR")}{o.delivery_date ? ` · İstenen: ${o.delivery_date}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-5 flex-wrap">
                  <div className="text-right"><p className="text-[10px] font-mono uppercase text-[#6B5B4A]">Verilen</p><p className="font-mono text-lg font-bold">{ordered}</p></div>
                  <div className="text-right"><p className="text-[10px] font-mono uppercase text-[#047857]">Gelen</p><p className="font-mono text-lg font-bold text-[#047857]" data-testid={`sole-order-received-${o.id}`}>{got}</p></div>
                  <div className="text-right"><p className="text-[10px] font-mono uppercase text-[#B45309]">Kalan</p><p className="font-mono text-lg font-bold text-[#B45309]" data-testid={`sole-order-remaining-${o.id}`}>{ordered - got}</p></div>
                  {o.status !== "completed" && (
                    <Button onClick={() => setReceiving(o)} data-testid={`sole-order-receive-${o.id}`}
                      className="h-9 bg-[#047857] hover:bg-[#065F46] text-white"><PackageCheck className="w-4 h-4 mr-1" /> Gelen Taban Gir</Button>
                  )}
                  <a href={`/taban-siparis-fis/${o.id}`} target="_blank" rel="noreferrer" data-testid={`sole-order-print-${o.id}`}
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-[#DCCFB8] text-[#B45309] hover:bg-[#EFE6D6] text-sm"><Printer className="w-4 h-4" /> Yazdır</a>
                  {got === 0 && (
                    <button onClick={() => remove(o.id)} data-testid={`sole-order-delete-${o.id}`} className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto rounded-md border border-[#E8DDCA]">
                <table className="w-full text-xs zs-table">
                  <thead className="bg-[#EFE6D6]">
                    <tr>
                      <th className="text-left px-3 py-2">Taban</th>
                      {SIZES.map((s) => <th key={s} className="text-center px-1 py-2">N°{s}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {groupLines(o.items).map((g) => (
                      <tr key={g.key} className="border-t border-[#E8DDCA]">
                        <td className="px-3 py-2 font-mono font-semibold text-[#B45309] whitespace-nowrap">{g.code}{g.color ? ` · ${g.color}` : ""}</td>
                        {SIZES.map((s) => {
                          const it = g.sizes[s];
                          if (!it) return <td key={s} className="text-center text-[#A8977F]">·</td>;
                          const done = (it.received_qty || 0) >= it.qty;
                          return (
                            <td key={s} className={`text-center font-mono ${done ? "text-[#047857]" : ""}`} title="gelen / verilen">
                              <span className="font-bold">{it.received_qty || 0}</span>/{it.qty}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {receiptsByOrder[o.id]?.length > 0 && (
                <div className="mt-2 text-xs" data-testid={`sole-order-deliveries-${o.id}`}>
                  <span className="font-semibold text-[#047857]">Teslimatlar: </span>
                  {receiptsByOrder[o.id].map((r) => (
                    <button key={r.id} type="button" onClick={() => setEditing({ receipt: r, order: o })} title="Düzenle"
                      data-testid={`sole-delivery-edit-${r.id}`}
                      className="inline-flex items-center gap-1 mr-2 mb-1 px-2 py-0.5 rounded border border-[#047857]/40 font-mono hover:bg-[#047857]/10">
                      {new Date(r.created_at).toLocaleDateString("tr-TR")} +{r.items.reduce((a, b) => a + b.qty, 0)}{r.note && !r.note.startsWith("Taban siparişi #") ? ` (${r.note})` : ""}
                      <Pencil className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              )}
              {o.note && <p className="text-xs text-[#8C7B68] italic mt-2">&ldquo;{o.note}&rdquo;</p>}
            </div>
          );
        })}
      </div>

      <SoleOrderForm open={formOpen} onOpenChange={setFormOpen} onCreated={load} orders={orders} />
      <SoleReceiptEditDialog receipt={editing?.receipt} order={editing?.order} onOpenChange={(v) => { if (!v) setEditing(null); }} onSaved={load} />
      <SoleReceiveDialog order={receiving} onOpenChange={(v) => { if (!v) setReceiving(null); }} onSaved={load} />
    </div>
  );
}
